import React from 'react';
import { useQuery } from '@apollo/client';

import { ProductCarousel } from 'src/components/carousel-v2/types/product-carousel';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useFiltersForSponsoredResources } from 'src/dispensary/hooks/use-filters-for-sponsored-resources';
import usePaths from 'src/hooks/use-paths';
import { AdInventoryId, GetSponsoredBrandDocument } from 'src/gql/graphql';
import { ADS_FETCH_POLICY } from 'src/utils/ads/helpers';
import { useSponsoredBrandProductsV2 } from '../shared/use-sponsored-brand-products-v2';

export const HomePageSponsoredBrandCarousel = (): JSX.Element | null => {
  const { href } = usePaths({ brands: true });
  const { dispensary } = useDispensary();

  // Only the menu type is forwarded. The rest of the sponsored-resource filters (category,
  // subcategory, brands, potency) describe a category-page context and would over-constrain the
  // auction here for no benefit.
  //
  // Without this the server falls back to the dispensary default, which is deliberately `rec`, so
  // medical shoppers at dual-menu stores never saw medical-only brand ads. Sending it also lets
  // Armageddon reject ineligible items before approving a decision, rather than relying on them
  // being dropped during hydration afterwards.
  const { activeMenuType } = useFiltersForSponsoredResources();

  const sponsoredBrandQuery = useQuery(GetSponsoredBrandDocument, {
    fetchPolicy: ADS_FETCH_POLICY,
    variables: {
      input: {
        dispensaryId: dispensary.id,
        inventoryId: AdInventoryId.homeCarouselAuc,
        minimumProducts: 3,
        ...(activeMenuType ? { filters: { menuType: activeMenuType } } : {}),
      },
    },
  });

  const {
    error,
    loading,
    products,
    handleProductClick,
    handleQuickAddToCart,
    handleViewAllClick,
    viewportVisibility,
    handleProductImpression,
    brandSlug,
  } = useSponsoredBrandProductsV2({ sponsoredBrandQuery, adPlacement: 'home.carousel' });

  if (!products.length || error) {
    return null;
  }

  if (!brandSlug) {
    console.error('missing brandSlug');
    return null;
  }

  const brandHref = `${href}/${brandSlug}`;

  return (
    <ProductCarousel
      hideSponsoredTag
      href={brandHref}
      isBottomSubTitle
      isLoading={loading}
      onProductClick={handleProductClick}
      onProductView={handleProductImpression}
      onQuickAddToCart={handleQuickAddToCart}
      onViewAllClick={handleViewAllClick}
      products={products}
      ref={viewportVisibility.ref}
      subTitle='Sponsored'
      title='Featured Brand'
    />
  );
};
